import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ImprovedNoise } from 'three/examples/jsm/math/ImprovedNoise';

const ImprovedNoiseParticles: React.FC<{ count?: number }> = ({ count = 2000 }) => {
    const pointsRef = useRef<THREE.Points>(null!);
    const materialRef = useRef<THREE.PointsMaterial>(null!);
    const noise = useMemo(() => new ImprovedNoise(), []);
    const baseSize = useMemo(() => 0.5 + Math.random() * 1.5, []);

    const positions = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            arr[i * 3] = (Math.random() - 0.5) * 20;
            arr[i * 3 + 1] = (Math.random() - 0.5) * 12;
            arr[i * 3 + 2] = 0;
        }
        return arr;
    }, [count]);

    const colors = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const v = 0.5 + Math.random() * 0.5;
            arr[i * 3] = v;
            arr[i * 3 + 1] = v;
            arr[i * 3 + 2] = v;
        }
        return arr;
    }, [count]);

    useFrame(({ clock }) => {
        const geo = pointsRef.current.geometry as THREE.BufferGeometry;
        const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
        const t = clock.getElapsedTime() * 0.2;

        for (let i = 0; i < count; i++) {
            const ix = i * 3;
            let x = posAttr.array[ix];
            let y = posAttr.array[ix + 1];
            const n = noise.noise(x * 0.1, y * 0.1, t);
            const angle = n * Math.PI * 2;
            const speed = 0.005;
            x += Math.cos(angle) * speed;
            y += Math.sin(angle) * speed;
            if (x < -10) x = 10; else if (x > 10) x = -10;
            if (y < -6) y = 6; else if (y > 6) y = -6;
            posAttr.array[ix] = x;
            posAttr.array[ix + 1] = y;
        }
        posAttr.needsUpdate = true;

        materialRef.current.size = baseSize * (1 + 0.3 * Math.sin(clock.getElapsedTime() * 3));
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
                <bufferAttribute attach="attributes-color" args={[colors, 3]} />
            </bufferGeometry>
            <pointsMaterial
                ref={materialRef}
                vertexColors={true}
                size={baseSize}
                sizeAttenuation={false}
                blending={THREE.AdditiveBlending}
                transparent={true}
                opacity={1}
            />
        </points>
    );
};

export default ImprovedNoiseParticles;